import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { isApiError, refusalReasonOf } from '@eis/core';
import { LEAD_REASONS } from './types';

/**
 * The lead refusals as the screens read them (SDD-013 §7): a 409 carrying its reason code in an
 * ErrorInfo detail, and — for LEAD_PIN_STALE — a typed `LeadPinStale` detail with both pins and
 * both premiums, so the accept dialog can show two prices rather than parse a sentence.
 */

const LEAD_PIN_STALE_TYPE = 'insurance.instance.v1.LeadPinStale';

/** The reason a lead mutation was refused with, or undefined for any other error. */
export const leadReasonOf = (error: unknown): string | undefined => refusalReasonOf(error);

export const isLeadRefusal = (error: unknown, reason: string): boolean =>
  leadReasonOf(error) === reason;

export interface PinStaleDetail {
  readonly pinned: InstanceApi.ConfigurationPin;
  readonly fresh: InstanceApi.ConfigurationPin;
  readonly pinnedPremium: InstanceApi.Premium;
  readonly refreshedPremium: InstanceApi.Premium;
}

/** The typed half of a LEAD_PIN_STALE refusal, or undefined when the error is anything else. */
export function pinStaleDetailOf(error: unknown): PinStaleDetail | undefined {
  if (!isApiError(error) || leadReasonOf(error) !== LEAD_REASONS.pinStale) return undefined;
  const body: unknown = error.body;
  if (typeof body !== 'object' || body === null) return undefined;
  const details: unknown = (body as { details?: unknown }).details;
  if (!Array.isArray(details)) return undefined;
  const detail = details.find((entry): entry is Record<string, unknown> => {
    if (typeof entry !== 'object' || entry === null) return false;
    const type: unknown = (entry as Record<string, unknown>)['@type'];
    return typeof type === 'string' && type.endsWith(LEAD_PIN_STALE_TYPE);
  });
  if (detail === undefined) return undefined;
  const pin = (value: unknown): InstanceApi.ConfigurationPin =>
    typeof value === 'object' && value !== null ? value : {};
  const premium = (value: unknown): InstanceApi.Premium =>
    typeof value === 'object' && value !== null ? value : {};
  return {
    pinned: pin(detail.pinned),
    fresh: pin(detail.fresh),
    pinnedPremium: premium(detail.pinnedPremium),
    refreshedPremium: premium(detail.refreshedPremium),
  };
}

/** The refusal's human sentence — the service's message — for an error the screen has no better words for. */
export function refusalMessageOf(error: unknown): string {
  if (!isApiError(error)) return error instanceof Error ? error.message : '';
  const body: unknown = error.body;
  const message: unknown =
    typeof body === 'object' && body !== null ? (body as { message?: unknown }).message : undefined;
  return typeof message === 'string' ? message : error.message;
}
